import React, { useState, useEffect } from "react";
import { BrandButton as Button, RolesSelector } from "@/components";
import HospiceSearchableSelect from "@/components/HospiceSearchableSelect";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
export default function EditUserForm({ selectedUser, hospices, roles = [], onSave, onCancel, isLoading }) {
  const [formData, setFormData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    enableBypassSurveyValidation: false,
    enableMultiSurveyExperience: false
  });
  
  // Hospice-role assignments: [{hospiceId, hospiceName, roleIds: [...]}]
  const [assignments, setAssignments] = useState([]);
  const [editingHospiceId, setEditingHospiceId] = useState(null);
  const [editingRoleIds, setEditingRoleIds] = useState([]);
  
  // New assignment fields
  const [newHospiceId, setNewHospiceId] = useState("");
  const [newRoleIds, setNewRoleIds] = useState([]);
  
  const [showAlert, setShowAlert] = useState(false);

  // Update form when selectedUser prop changes
  useEffect(() => {
    if (selectedUser) {
      console.log('EditUserForm useEffect - selectedUser:', selectedUser);
      console.log('EditUserForm useEffect - selectedUser.hospiceRoles:', selectedUser.hospiceRoles);
        console.log('EditUserForm useEffect - formData set to:', {
          enableBypassSurveyValidation: Boolean(selectedUser.enableBypassSurveyValidation ?? selectedUser.EnableBypassSurveyValidation),
          enableMultiSurveyExperience: Boolean(selectedUser.enableMultiSurveyExperience ?? selectedUser.EnableMultiSurveyExperience)
        });
      setFormData({
        id: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        email: selectedUser.email || "",
        enableBypassSurveyValidation: Boolean(selectedUser.enableBypassSurveyValidation ?? selectedUser.EnableBypassSurveyValidation),
        enableMultiSurveyExperience: Boolean(selectedUser.enableMultiSurveyExperience ?? selectedUser.EnableMultiSurveyExperience)
       
      });
      
      // Group hospiceRoles by hospice
      const hospiceRoleMap = {};
      (selectedUser.hospiceRoles || []).forEach(hr => {
        console.log('Processing hospiceRole:', hr);
        // Handle both casing: backend sends lowercase on GET, expects PascalCase on PUT
        const hospiceId = hr.HospiceId || hr.hospiceId;
        const roleId = hr.RoleId || hr.roleId;
        if (!hospiceId || !roleId) return;
        
        if (!hospiceRoleMap[hospiceId]) {
          hospiceRoleMap[hospiceId] = [];
        }
        hospiceRoleMap[hospiceId].push(roleId);
      });
      
      console.log('hospiceRoleMap:', hospiceRoleMap);
      
      // Convert to assignments array
      const assignmentsArray = Object.entries(hospiceRoleMap).map(([hospiceId, roleIds]) => {
        const hospice = hospices.find(h => h.id === hospiceId);
        return {
          hospiceId,
          hospiceName: hospice?.name || hospiceId,
          roleIds
        };
      });
      
      console.log('assignmentsArray:', assignmentsArray);
      
      setAssignments(assignmentsArray);
      setShowAlert(false);
    } else {
      setShowAlert(true);
    }
  }, [selectedUser, hospices]);

  const handleReset = () => {
    if (selectedUser) {
      setFormData({
        id: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        email: selectedUser.email || "",
        enableBypassSurveyValidation: Boolean(selectedUser.enableBypassSurveyValidation ?? selectedUser.EnableBypassSurveyValidation),
        enableMultiSurveyExperience: Boolean(selectedUser.enableMultiSurveyExperience ?? selectedUser.EnableMultiSurveyExperience)
      });
      
      // Reset assignments
      const hospiceRoleMap = {};
      (selectedUser.hospiceRoles || []).forEach(hr => {
        const hospiceId = hr.HospiceId || hr.hospiceId;
        const roleId = hr.RoleId || hr.roleId;
        if (!hospiceRoleMap[hospiceId]) {
          hospiceRoleMap[hospiceId] = [];
        }
        hospiceRoleMap[hospiceId].push(roleId);
      });
      
      const assignmentsArray = Object.entries(hospiceRoleMap).map(([hospiceId, roleIds]) => {
        const hospice = hospices.find(h => h.id === hospiceId);
        return {
          hospiceId,
          hospiceName: hospice?.name || hospiceId,
          roleIds
        };
      });
      
      setAssignments(assignmentsArray);
      setNewHospiceId("");
      setNewRoleIds([]);
      setEditingHospiceId(null);
      setEditingRoleIds([]);
    }
  };

  const addAssignment = () => {
    if (!newHospiceId || newRoleIds.length === 0) {
      return;
    }
    
    const hospice = hospices.find(h => h.id === newHospiceId);
    const newAssignment = {
      hospiceId: newHospiceId,
      hospiceName: hospice?.name || newHospiceId,
      roleIds: newRoleIds
    };
    
    setAssignments([...assignments, newAssignment]);
    setNewHospiceId("");
    setNewRoleIds([]);
  };

  const removeAssignment = (hospiceId) => {
    setAssignments(assignments.filter(a => a.hospiceId !== hospiceId));
  };

  const startEdit = (hospiceId) => {
    const assignment = assignments.find(a => a.hospiceId === hospiceId);
    if (assignment) {
      setEditingHospiceId(hospiceId);
      setEditingRoleIds([...assignment.roleIds]);
    }
  };

  const saveEdit = () => {
    setAssignments(assignments.map(a => 
      a.hospiceId === editingHospiceId 
        ? { ...a, roleIds: editingRoleIds }
        : a
    ));
    setEditingHospiceId(null);
    setEditingRoleIds([]);
  };

  const cancelEdit = () => {
    setEditingHospiceId(null);
    setEditingRoleIds([]);
  };

  const handleSave = () => {
    // Flatten assignments back to HospiceRoles format
    const hospiceRoles = assignments.flatMap(assignment =>
      assignment.roleIds.map(roleId => ({
        HospiceId: assignment.hospiceId,
        RoleId: roleId
      }))
    );
    
    const dataToSave = {
      ...formData,
      hospiceRoles
    };
    
    console.log('EditUserForm handleSave - dataToSave:', dataToSave);
    onSave(dataToSave);
  };

  // Check if form has changes from original
  const originalAssignmentsStr = JSON.stringify(
    Object.entries(
      (selectedUser?.hospiceRoles || []).reduce((acc, hr) => {
        const hId = hr.HospiceId || hr.hospiceId;
        const rId = hr.RoleId || hr.roleId;
        if (!acc[hId]) acc[hId] = [];
        acc[hId].push(rId);
        return acc;
      }, {})
    ).map(([hId, rIds]) => ({ hospiceId: hId, roleIds: rIds.sort() }))
    .sort((a, b) => a.hospiceId.localeCompare(b.hospiceId))
  );
  
  const currentAssignmentsStr = JSON.stringify(
    assignments.map(a => ({ hospiceId: a.hospiceId, roleIds: [...a.roleIds].sort() }))
    .sort((a, b) => a.hospiceId.localeCompare(b.hospiceId))
  );
  
  const hasChanges = selectedUser && (
    formData.firstName !== (selectedUser.firstName || "") ||
    formData.lastName !== (selectedUser.lastName || "") ||
    formData.email !== (selectedUser.email || "") ||
    formData.enableBypassSurveyValidation !== Boolean(selectedUser.enableBypassSurveyValidation ?? selectedUser.EnableBypassSurveyValidation) ||
    formData.enableMultiSurveyExperience !== Boolean(selectedUser.enableMultiSurveyExperience ?? selectedUser.EnableMultiSurveyExperience) ||
    originalAssignmentsStr !== currentAssignmentsStr
  );

  return (
    <div>
      {selectedUser ? (
        <div className="row g-5">
          {/* Left column: Basic Info + Action Buttons */}
          <div className="col-md-6">
            <p className="text-muted">Basic Info</p>

            <div className="mb-3">
              <label className="form-label ">First Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label ">Last Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label ">Email</label>
              <input
                type="email"
                className="form-control"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={isLoading}
              />
            </div>

            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: formData.enableBypassSurveyValidation ? '#fff0e6' : '#fff8f0', borderColor: '#f16d09' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="form-check mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="bypassValidation"
                    checked={formData.enableBypassSurveyValidation}
                    onChange={(e) => setFormData({ ...formData, enableBypassSurveyValidation: e.target.checked })}
                    disabled={isLoading}
                  />
                  <label className="form-check-label " htmlFor="bypassValidation">
                    Skip validation enforcement
                    {formData.enableBypassSurveyValidation && (
                      <span className="ms-2 badge" style={{ backgroundColor: '#f16d09', fontSize: '0.7rem' }}>ACTIVE</span>
                    )}
                  </label>
                </div>
              </div>
              <small className="text-muted d-block mt-1">
                Allow this user to submit surveys without being blocked by validation errors.
              </small>
            </div>

            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: formData.enableMultiSurveyExperience ? '#fff8f0' : '#fff8f0', borderColor: '#f16d09' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="form-check mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="multiSurveyExperience"
                    checked={formData.enableMultiSurveyExperience}
                    onChange={(e) => setFormData({ ...formData, enableMultiSurveyExperience: e.target.checked })}
                    disabled={isLoading}
                  />
                  <label className="form-check-label " htmlFor="multiSurveyExperience">
                    Enable multi-hospice survey experience
                    {formData.enableMultiSurveyExperience && (
                      <span className="ms-2 badge" style={{ backgroundColor: '#f16d09', fontSize: '0.7rem' }}>ACTIVE</span>
                    )}
                  </label>
                </div>
              </div>
              <small className="text-muted d-block mt-1">
                Allow this user to complete surveys for multiple hospices simultaneously.
              </small>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2 mt-4">
              <Button 
                type="button"
                onClick={handleSave}
                disabled={!hasChanges || isLoading}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
              <Button 
                type="button"
                onClick={handleReset}
                disabled={!hasChanges || isLoading}
              >
                Reset
              </Button>
              {onCancel && (
                <Button 
                  type="button"
                  onClick={onCancel}
                  disabled={isLoading}
                  variant="secondary"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Right column: Hospice-Role Assignments */}
          <div className="col-md-6">
          {/* Current Assignments */}
          <div className="mb-4">
            <h5 className=" mb-3">Current Hospice-Role Assignments</h5>
            {assignments.length === 0 ? (
              <p className="text-muted">No assignments yet. Add one below.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {assignments.map((assignment) => (
                  <div 
                    key={assignment.hospiceId}
                    className="card p-3"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    {editingHospiceId === assignment.hospiceId ? (
                      // Edit mode
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0" style={{ color: '#F16D09' }}>{assignment.hospiceName}</h6>
                          <div className="d-flex gap-1">
                            <Button
                              onClick={saveEdit}
                              disabled={editingRoleIds.length === 0}
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                            >
                              Save
                            </Button>
                            <Button
                              onClick={cancelEdit}
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                        <RolesSelector
                          selectedRoles={editingRoleIds}
                          onRolesChange={setEditingRoleIds}
                          title=""
                          description="Update roles for this hospice"
                          required={false}
                        />
                      </>
                    ) : (
                      // View mode
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0" style={{ color: '#F16D09' }}>{assignment.hospiceName}</h6>
                          <div className="d-flex gap-1">
                            <Button
                              onClick={() => startEdit(assignment.hospiceId)}
                              disabled={isLoading}
                              style={{ background: '#F16D09', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => removeAssignment(assignment.hospiceId)}
                              disabled={isLoading}
                              style={{ background: '#dc3545', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                            >
                              ×
                            </Button>
                          </div>
                        </div>
                        <ul className="mb-0" style={{ paddingLeft: '1.25rem' }}>
                          {assignment.roleIds.map(roleId => {
                            const role = roles.find(r => r.id === roleId);
                            return (
                              <li key={roleId} style={{ color: '#495057' }}>
                                {role?.name || roleId}
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New Assignment */}
          <div className="mb-4 card p-3" style={{ backgroundColor: '#e7f3ff' }}>
            <h5 className=" mb-3">Add New Hospice-Role Assignment</h5>
            
            <div className="mb-3">
              <label className="form-label ">Select Hospice</label>
              <HospiceSearchableSelect
                hospices={hospices.filter(h => !assignments.some(a => a.hospiceId === h.id))}
                value={newHospiceId}
                onChange={(hospiceId) => setNewHospiceId(hospiceId)}
                placeholder="Choose a hospice..."
                disabled={isLoading}
              />
            </div>

            {newHospiceId && (
              <div className="mb-3">
                <RolesSelector
                  selectedRoles={newRoleIds}
                  onRolesChange={setNewRoleIds}
                  title="Select Roles"
                  description="Choose roles for this hospice"
                  required={false}
                />
              </div>
            )}

            <Button
              onClick={addAssignment}
              disabled={!newHospiceId || newRoleIds.length === 0 || isLoading}
              style={{ background: '#f16d09' }}
            >
              Add Assignment
            </Button>
          </div>

          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-muted mb-3">No user selected. Click the button below to go back and select a user.</p>
          {onCancel && (
            <Button 
              type="button"
              onClick={onCancel}
            >
              Back to User Selection
            </Button>
          )}
        </div>
      )}
    </div>
  );
}